import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Package } from "lucide-react";
import { api } from "../lib/api";
import CountdownTimer from "../components/CountdownTimer";
import ProductCard from "../components/ProductCard";
import WaitlistForm from "../components/WaitlistForm";
import TireSmoke from "../components/TireSmoke";

const HERO_BG = "https://customer-assets.emergentagent.com/job_speedway-drops/artifacts/jzj43oya_IMG_3191.png";
const TEXTURE = "https://static.prod-images.emergentagent.com/jobs/5ad61a19-a61d-414f-a590-c718e07fed02/images/22e0cbff90d261c92d8762de6150ae301e95551cfcbf3e4c2aab80f939644a6c.png";

const YoutubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.6 3.6 12 3.6 12 3.6s-7.6 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.8.5 9.4.5 9.4.5s7.6 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.6 15.6V8.4l6.4 3.6-6.4 3.6z"/>
  </svg>
);

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, p, g] = await Promise.all([
        api.get("/settings").then((r) => r.data),
        api.get("/products").then((r) => r.data),
        api.get("/gallery").then((r) => r.data),
      ]);
      setSettings(s); setProducts(p); setGallery(g);
    })();
  }, []);

  const featuredWheel = products.find((p) => p.id === settings?.featured_wheel_id) || products.find((p) => p.category === "wheels");
  const featuredApparel = products.find((p) => p.id === settings?.featured_apparel_id) || products.find((p) => p.category === "apparel" && !/^kids\b/i.test(p?.name));
  const wheels = products.filter((p) => p.category === "wheels").slice(0, 3);
  const apparel = products.filter((p) => p.category === "apparel" && !/^kids\b/i.test(p?.name)).slice(0, 3);
  const kidsHero = products.find((p) => p.category === "apparel" && /^kids\b/i.test(p?.name) && p?.images?.length);
  const wheelsHero = "https://customer-assets.emergentagent.com/job_speedway-drops/artifacts/uoxreug5_IMG_3888.jpeg";

  return (
    <div data-testid="home-page" className="bg-ink text-white">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden noise">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover scale-110" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/30 to-transparent" />
        </div>
        <TireSmoke />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
          <div className="flex items-center gap-3 mb-6 animate-fade-up">
            <span className="h-2 w-2 bg-adrenaline animate-pulse-red rounded-full" />
            <span className="text-mono text-xs uppercase tracking-[0.4em] text-zinc-400">{settings?.drop_name || "Drop 003 Loading"}</span>
          </div>
          <h1 data-testid="hero-headline" className="text-display text-6xl sm:text-7xl md:text-8xl lg:text-[9rem] text-white leading-[0.85] uppercase animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Texas Born.<br /><span className="text-adrenaline red-glow">Street</span> Official.
          </h1>
          <p data-testid="hero-sub" className="mt-8 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
            Limited shirts for people who live speed, street cars, and drag racing culture. Texas-built. Limited quantities. No restocks.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <a href="#waitlist" data-testid="cta-join-drop" className="bg-adrenaline text-white px-8 py-4 text-heading uppercase tracking-[0.2em] text-sm hover:bg-red-600 transition-colors flex items-center gap-2 justify-center btn-glow">
              Join the Drop List <ArrowRight size={16} />
            </a>
            <Link to="/apparel" data-testid="cta-shop-drops" className="border border-white/30 text-white px-8 py-4 text-heading uppercase tracking-[0.2em] text-sm hover:border-white hover:bg-white hover:text-black transition-all flex items-center gap-2 justify-center">
              Shop Drops <ArrowRight size={16} />
            </Link>
          </div>
          {settings?.countdown_target && (
            <div className="mt-16 animate-fade-up" style={{ animationDelay: "0.4s" }}>
              <CountdownTimer target={settings.countdown_target} dropName={settings.drop_name} />
            </div>
          )}
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-carbon border-y border-white/5 overflow-hidden py-4">
        <div className="flex gap-12 animate-marquee whitespace-nowrap text-display text-3xl tracking-widest text-zinc-700">
          {Array.from({ length: 2 }).map((_, j) => (
            <div key={`marquee-row-${j}`} className="flex gap-12 items-center">
              {["TEXAS BORN", "★", "STREET OFFICIAL", "★", "DRAG STRIP READY", "★", "LIMITED DROPS", "★", "NO RESTOCKS", "★"].map((t, i) => (
                <span key={`${j}-${i}`} className={t === "★" ? "text-adrenaline" : ""}>{t}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* FEATURED APPAREL — FLAGSHIP DROP */}
      {featuredApparel && (
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-carbon to-ink" />
          <div className="absolute inset-0 noise opacity-40" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-5 gap-10 md:gap-16 items-center">
              <div className="md:col-span-3 relative">
                <div className="aspect-[4/5] bg-black overflow-hidden hover-zoom border border-white/10">
                  <img src={featuredApparel.images?.[0]} alt={featuredApparel.name} className="w-full h-full object-cover" />
                </div>
                <div className="absolute -top-3 -left-3 px-4 py-2 bg-adrenaline text-white text-mono text-[10px] uppercase tracking-[0.3em] btn-glow">★ Flagship Drop</div>
              </div>
              <div className="md:col-span-2">
                <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline mb-5 flex items-center gap-3">
                  <span className="h-2 w-2 bg-adrenaline animate-pulse-red rounded-full" /> First Release · Limited
                </div>
                <h2 className="text-display text-5xl md:text-6xl lg:text-7xl leading-none uppercase">{featuredApparel.name}</h2>
                <div className="mt-6 inline-block border-l-2 border-adrenaline pl-4 py-1">
                  <div className="text-heading uppercase text-white text-xl tracking-wider">Built for Speed.</div>
                  <div className="text-heading uppercase text-adrenaline text-xl tracking-wider">Fueled by Passion.</div>
                </div>
                <p className="text-zinc-400 leading-relaxed text-base mt-8 max-w-md">{featuredApparel.description}</p>
                <dl className="grid grid-cols-3 gap-4 mt-8 text-mono">
                  <Spec k="Fit" v="Oversized" />
                  <Spec k="Weight" v="240gsm" />
                  <Spec k="Sizes" v="S — XXL" />
                </dl>
                <div className="mt-10 flex items-center gap-6">
                  <div className="text-display text-5xl">${featuredApparel.price.toFixed(0)}</div>
                  <Link to={`/product/${featuredApparel.id}`} data-testid="featured-cta" className="bg-adrenaline text-white px-7 py-3.5 text-heading uppercase tracking-[0.2em] text-sm hover:bg-red-600 transition-colors flex items-center gap-2 btn-glow">
                    Cop the Drop <ArrowRight size={16} />
                  </Link>
                </div>
                <div className="mt-8 grid grid-cols-3 gap-3 text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                  <div>★ Texas Printed</div>
                  <div>★ Limited Run</div>
                  <div>★ No Restocks</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WHEELS — Signature Mesh Series */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-wheels-section">
        <SectionHead label="Signature Mesh Series" title="Wheels" link="/wheels" />
        <Link to="/wheels" data-testid="home-wheels-card" className="group relative aspect-[21/9] md:aspect-[21/7] overflow-hidden bg-black block mt-10">
          <img src={wheelsHero} alt="Lone Star Speed Signature Mesh Wheels" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">Built for Speed. Made to Win.</div>
            <div className="text-display text-4xl md:text-6xl uppercase mt-2 leading-none">Custom Forged Wheels</div>
            <div className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-300 mt-4 flex items-center gap-2 group-hover:text-adrenaline transition-colors">
              One Design · Four Finishes · From $2,749 / Wheel <ArrowRight size={14} />
            </div>
          </div>
        </Link>
      </section>

      {/* KIDS */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="home-kids-section">
        <SectionHead label="Next Generation" title="Kids Collection" link="/apparel?filter=kids" />
        <Link to="/apparel?filter=kids" data-testid="home-kids-card" className="group relative aspect-[21/9] md:aspect-[21/7] overflow-hidden bg-black block mt-10">
          {kidsHero?.images?.[0] ? (
            <img src={kidsHero.images[0]} alt="Lone Star Speed Kids" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
          ) : (
            <div className="w-full h-full grid place-items-center text-mono text-xs uppercase tracking-[0.3em] text-zinc-600">Kids Collection</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
            <div className="text-mono text-[11px] uppercase tracking-[0.4em] text-adrenaline">Built For The Little Legends</div>
            <div className="text-display text-4xl md:text-6xl uppercase mt-2 leading-none">Kids Tees</div>
            <div className="text-mono text-[11px] uppercase tracking-[0.3em] text-zinc-300 mt-4 flex items-center gap-2 group-hover:text-adrenaline transition-colors">
              Sizes 2T — YL · $28 <ArrowRight size={14} />
            </div>
          </div>
        </Link>
      </section>

      {/* APPAREL GRID */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead label="Garments & Headwear" title="Apparel Drops" link="/apparel" />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 mt-10">
          {apparel.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* GALLERY / BUILDS */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead label="From the Street" title="Builds & Battle Stories" link="/gallery" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-10">
          {gallery.slice(0, 4).map((g) => (
            <div key={g.id} className="aspect-square overflow-hidden hover-zoom bg-carbon group relative">
              <img src={g.image} alt={g.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center text-mono text-xs uppercase tracking-[0.3em] text-white p-4 text-center">{g.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF — Merch reviews (shipping + quality) */}
      <section className="py-24 bg-carbon border-y border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead label="From The Customers" title="Real Reviews. Real Owners." />
          <div className="grid md:grid-cols-3 gap-1 mt-10">
            {[
              { icon: Truck, q: "Ordered Monday, on my doorstep Wednesday. Texas to Cali in 2 days — these guys ship fast.", a: "— Mike R., Long Beach", tag: "Fast Shipping" },
              { icon: ShieldCheck, q: "The cotton is way heavier than I expected. Screen print is crisp, no cracking after multiple washes. Real quality.", a: "— Devon T., Austin", tag: "Premium Quality" },
              { icon: Package, q: "Packaging was on point — branded mailer, thank-you card, sticker pack. Felt like opening a real drop, not a Shopify box.", a: "— Carlos M., Dallas", tag: "Drop Experience" },
            ].map((t) => (
              <div key={t.tag} className="bg-ink p-8 border border-white/5 hover:border-adrenaline/40 transition-colors">
                <div className="flex items-center justify-between mb-5">
                  <t.icon className="text-adrenaline" size={22} />
                  <span className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{t.tag}</span>
                </div>
                <p className="text-zinc-200 text-lg leading-relaxed">"{t.q}"</p>
                <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-6">{t.a}</div>
              </div>
            ))}
          </div>
          {/* Fast shipping strip */}
          <div className="grid md:grid-cols-4 gap-1 mt-1">
            <ShipPromise icon={Truck} title="2–4 Day Shipping" sub="Texas to all 50 states" />
            <ShipPromise icon={ShieldCheck} title="240gsm Heavyweight" sub="Garment-dyed, no shrink" />
            <ShipPromise icon={Package} title="Branded Drop Box" sub="Sticker pack included" />
            <ShipPromise icon={ArrowRight} title="No-Hassle Returns" sub="14 days, prepaid label" />
          </div>
        </div>
      </section>

      {/* MUSCLE CAR BUILDS */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead label="American Iron" title="Muscle Car Builds" />
        <p className="text-zinc-400 max-w-xl mt-6 text-base">Carbureted, big block, sideways. Mustangs, Camaros, Chargers, Chevelles, GTOs — if it's loud and rear-wheel-drive, it lives here. Got a build? Tag <span className="text-white">@lonestarspeed</span>.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-10">
          {[1, 2, 3, 4].map((i) => <BuildSlot key={i} label={`Slot 0${i}`} category="Muscle" />)}
        </div>
      </section>

      {/* LATE MODEL BUILDS */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHead label="Modern Performance" title="Late Model Builds" />
        <p className="text-zinc-400 max-w-xl mt-6 text-base">DI'd, twin-turbo, dual-clutch. C8 Corvettes, Hellcats, GT500s, Supras, GR Corollas — the new generation laying down 9-second passes on pump gas.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1 mt-10">
          {/* The verified C8 — user's hero photo */}
          <div className="relative aspect-square overflow-hidden bg-carbon hover-zoom group">
            <img src={HERO_BG} alt="C8 Corvette LSS-001" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-adrenaline">Featured</div>
              <div className="text-heading uppercase text-white mt-1 text-lg leading-tight">C8 Corvette · LSS-001</div>
            </div>
          </div>
          {[2, 3, 4].map((i) => <BuildSlot key={i} label={`Slot 0${i}`} category="Late Model" />)}
        </div>
      </section>

      {/* WAITLIST */}
      <section id="waitlist" className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={TEXTURE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink/80 to-ink" />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline mb-5">Join the Movement</div>
          <h2 className="text-display text-5xl md:text-7xl uppercase leading-none">First Access to Every Drop</h2>
          <p className="text-zinc-400 text-lg mt-6 max-w-xl mx-auto">Be the first to know when new wheels and limited apparel drop. Skip the queue.</p>
          <div className="mt-12 flex justify-center"><WaitlistForm /></div>
        </div>
      </section>
    </div>
  );
}

function Spec({ k, v }) {
  return (
    <div>
      <dt className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">{k}</dt>
      <dd className="text-white text-mono text-sm mt-1">{v}</dd>
    </div>
  );
}

function ShipPromise({ icon: Icon, title, sub }) {
  return (
    <div className="bg-ink p-6 border border-white/5 hover:border-adrenaline/40 transition-colors flex items-center gap-4">
      <span className="h-10 w-10 grid place-items-center border border-adrenaline/40 text-adrenaline shrink-0"><Icon size={18} /></span>
      <div>
        <div className="text-heading uppercase text-white tracking-wide text-sm">{title}</div>
        <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">{sub}</div>
      </div>
    </div>
  );
}

function BuildSlot({ label, category }) {
  return (
    <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-carbon to-ink border border-white/5 hover:border-adrenaline/40 transition-colors group flex flex-col items-center justify-center text-center p-6">
      <div className="absolute inset-0 noise opacity-30" />
      <div className="relative flex flex-col items-center">
        <span className="h-12 w-12 rounded-full border border-white/20 text-white grid place-items-center text-2xl group-hover:border-adrenaline group-hover:text-adrenaline transition-colors">+</span>
        <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-adrenaline mt-5">{category}</div>
        <div className="text-heading uppercase text-white text-base mt-2">Submit Your Build</div>
        <div className="text-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-3">Tag @lonestarspeed</div>
        <div className="text-mono text-[9px] uppercase tracking-[0.3em] text-zinc-700 mt-1">{label}</div>
      </div>
    </div>
  );
}

function SectionHead({ label, title, link }) {
  return (
    <div className="flex items-end justify-between gap-6">
      <div>
        <div className="text-mono text-xs uppercase tracking-[0.4em] text-adrenaline">{label}</div>
        <h2 className="text-display text-4xl md:text-6xl uppercase leading-none mt-2">{title}</h2>
      </div>
      {link && (
        <Link to={link} className="hidden md:inline text-heading uppercase tracking-[0.2em] text-sm text-zinc-400 hover:text-white whitespace-nowrap">See All →</Link>
      )}
    </div>
  );
}
